import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rootDivisions", "newDivisionTemplate", "parentDivisionId", "emptyState", "requireDivisionField", "formHeading", "requireDivisionLabel", "requireDivisionSelect"]
  static values = { tournamentId: String, editionId: String }

  connect() {
    // Initialize sortable for drag and drop
    if (typeof Sortable !== 'undefined') {
      this.initializeSortable()
    }
  }

  initializeSortable() {
    const containers = this.element.querySelectorAll('[id^="root_divisions"], [id^="sub_divisions_"]')
    
    containers.forEach(container => {
      new Sortable(container, {
        group: 'divisions',
        animation: 150,
        fallbackOnBody: true,
        swapThreshold: 0.65,
        handle: '.division-item',
        ghostClass: 'opacity-50',
        onEnd: (evt) => {
          this.updatePosition(evt)
        }
      })
    })
  }

  addRootDivision() {
    this.removeEmptyState()
    const template = this.newDivisionTemplateTarget.content.cloneNode(true)
    const form = template.querySelector('[data-new-division]')
    
    // Add unique ID for removal after creation
    form.id = "new_division_root"
    
    // Clear parent division ID
    const parentInput = form.querySelector('[data-divisions-target="parentDivisionId"]')
    if (parentInput) parentInput.value = ''
    
    // Set heading for root division
    const heading = form.querySelector('[data-divisions-target="formHeading"]')
    if (heading) heading.textContent = 'Add New Root Division'
    
    // Show required division field and populate options for root divisions
    const requireDivisionField = form.querySelector('[data-divisions-target="requireDivisionField"]')
    if (requireDivisionField) requireDivisionField.style.display = 'block'
    
    // Populate select options with available divisions
    this.populateRequireDivisionOptions(form)
    
    this.rootDivisionsTarget.appendChild(template)
  }

  addSubDivision(event) {
    const parentId = event.currentTarget.dataset.parentId
    const template = this.newDivisionTemplateTarget.content.cloneNode(true)
    const form = template.querySelector('[data-new-division]')
    
    // Get parent division name
    const parentDivisionElement = document.getElementById(`division_${parentId}`)
    const parentDivisionName = this.getParentDivisionName(parentDivisionElement)
    
    // Add unique ID for removal after creation
    form.id = `new_division_${parentId}`
    
    // Set parent division ID
    const parentInput = form.querySelector('[data-divisions-target="parentDivisionId"]')
    if (parentInput) parentInput.value = parentId
    
    // Set heading for sub-division
    const heading = form.querySelector('[data-divisions-target="formHeading"]')
    if (heading) heading.textContent = `Add Sub-Division to "${parentDivisionName}"`
    
    // Show required division field for sub-divisions and populate options
    const requireDivisionField = form.querySelector('[data-divisions-target="requireDivisionField"]')
    if (requireDivisionField) requireDivisionField.style.display = 'block'
    
    const requireDivisionLabel = form.querySelector('[data-divisions-target="requireDivisionLabel"]')
    if (requireDivisionLabel) requireDivisionLabel.textContent = `Required Division for Entry`
    
    // Populate select options with available divisions
    this.populateRequireDivisionOptions(form)
    
    const subDivisionsContainer = document.getElementById(`sub_divisions_${parentId}`)
    if (subDivisionsContainer) {
      subDivisionsContainer.appendChild(template)
    }
  }

  editDivision(event) {
    const divisionId = event.currentTarget.dataset.divisionId
    const form = document.getElementById(`edit_division_${divisionId}_form`)
    
    if (form) {
      form.classList.toggle('hidden')
    }
  }

  cancelEdit(event) {
    const divisionId = event.currentTarget.dataset.divisionId
    const form = document.getElementById(`edit_division_${divisionId}_form`)
    
    if (form) {
      form.classList.add('hidden')
    }
  }

  cancelNewDivision(event) {
    const form = event.target.closest('[data-new-division]')
    if (form) {
      form.remove()
    }
    
    // Show empty state if no divisions
    if (this.rootDivisionsTarget.children.length === 0) {
      this.showEmptyState()
    }
  }

  updatePosition(evt) {
    const divisionId = evt.item.dataset.divisionId
    const position = evt.newIndex
    
    // Send position update to server
    fetch(`/tournaments/${this.tournamentId}/manage/divisions/${divisionId}/reorder`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ position: position })
    })
  }

  removeEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.remove()
    }
  }

  showEmptyState() {
    if (!this.hasEmptyStateTarget) {
      this.rootDivisionsTarget.innerHTML = `
        <div class="text-center py-12 text-gray-500" data-divisions-target="emptyState">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
          </svg>
          <p class="mt-4">No divisions configured yet. Click "Add Root Division" to get started.</p>
        </div>
      `
    }
  }

  get tournamentId() {
    return this.tournamentIdValue || window.location.pathname.split('/')[2]
  }

  get editionId() {
    return this.editionIdValue || window.location.pathname.split('/')[4]
  }

  getParentDivisionName(parentElement) {
    if (!parentElement) return 'Unknown Division'
    
    // Look for the division name in the h3 element
    const nameElement = parentElement.querySelector('h3')
    if (nameElement) {
      return nameElement.textContent.trim()
    }
    
    return 'Unknown Division'
  }

  populateRequireDivisionOptions(form) {
    const select = form.querySelector('[data-divisions-target="requireDivisionSelect"]')
    if (!select) return
    
    // Clear existing options except "None"
    select.innerHTML = '<option value="">None</option>'
    
    // Get all existing divisions
    const allDivisions = this.element.querySelectorAll('[data-division-id]')
    
    allDivisions.forEach(divisionElement => {
      const divisionId = divisionElement.dataset.divisionId
      const divisionName = this.getParentDivisionName(divisionElement)
      
      if (divisionId && divisionName !== 'Unknown Division') {
        const option = document.createElement('option')
        option.value = divisionId
        option.textContent = divisionName
        select.appendChild(option)
      }
    })
  }
}